"use client";

import { useEffect, useState } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";

import { useMode } from "@demo/hooks/useMode";

import FeaturedCarousel from "./FeaturedCarousel";
import { useFeaturedTemplates } from "./useFeaturedTemplates";
import { useTemplateSelect } from "./useTemplateSelect";

type InlinePreviewProps = {
  isVisible: boolean;
  onTemplateSelect: () => void;
  onOpenExamplesPanel: () => void;
};

/**
 * Inline featured-examples preview at the bottom of the prompt area.
 *
 * The `xl:` styles below must stay aligned with `STACKED_LAYOUT_BREAKPOINT_PX`
 * so the band visuals match `FeaturedCarousel`'s layout swap.
 *
 * Visible content is gated on `mounted` so SSR doesn't ship the desktop-sized
 * variant on mobile before hydration swaps it. The outer wrapper stays
 * mounted so its layout slot doesn't reflow when the content fades in.
 */
const InlinePreview = ({ isVisible, onTemplateSelect, onOpenExamplesPanel }: InlinePreviewProps) => {
  const [mode] = useMode();
  const featuredTemplates = useFeaturedTemplates(mode);
  const handleSelect = useTemplateSelect(onTemplateSelect);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (featuredTemplates.length === 0) return null;

  return (
    <div
      // `inert` alone covers what `aria-hidden` would (focus + a11y tree).
      // Pairing them trips a Chrome warning when a descendant still has focus.
      className={cn(
        "absolute inset-x-3 bottom-3 transition-[opacity,transform] duration-200",
        isVisible ? "translate-x-0 opacity-100" : "-translate-x-2 opacity-0",
      )}
      inert={!isVisible}
    >
      {mounted && (
        <div className="flex flex-col gap-2 duration-300 animate-in fade-in xl:rounded-2xl xl:border xl:border-background-alt xl:bg-background xl:p-2 xl:shadow-[0_4px_2px_0_rgba(0,0,0,0.07)]">
          <div className="flex items-center justify-between pl-1 xl:pl-2">
            <span className="body">{strings.templates.title}</span>
            <Button
              className="legal"
              data-testid="templates-see-all-button"
              size="sm"
              type="button"
              variant="ghost"
              onClick={onOpenExamplesPanel}
            >
              <Icon aria-hidden="true" className="size-4" name="layout-grid" />
              <span className="ml-1">{strings.templates.seeAllLabel}</span>
            </Button>
          </div>
          <div className="overflow-hidden rounded-2xl border-4 border-background xl:rounded-none xl:border-0">
            <FeaturedCarousel templates={featuredTemplates} variant="inline" onSelect={handleSelect} />
          </div>
        </div>
      )}
    </div>
  );
};

export default InlinePreview;
